import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Image,
  SafeAreaView,
  ScrollView,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../utils/responsive';
import { NavigationProp } from '@react-navigation/native';
import OrnamentBackground from '../../components/OrnamentBackground';

const PhoneVerificationScreen = ({ navigation }: { navigation: NavigationProp<any> }) => {
  const [phoneNumber, setPhoneNumber] = useState('');

  const formatPhoneNumber = (input: string) => {
    // Remove all non-digit characters
    const cleaned = input.replace(/\D/g, '');
    
    // Format the phone number
    let formatted = '';
    if (cleaned.length > 0) {
      formatted = '(' + cleaned.substring(0, 3);
    }
    if (cleaned.length > 3) {
      formatted += ') ' + cleaned.substring(3, 6);
    }
    if (cleaned.length > 6) {
      formatted += '-' + cleaned.substring(6, 10);
    }
    return formatted;
  };

  const handlePhoneNumberChange = (input: string) => {
    // Format the input as the user types
    const formatted = formatPhoneNumber(input);
    setPhoneNumber(formatted);
  };

  const handleContinue = () => {
    // Remove all non-digit characters for validation
    const cleanedNumber = phoneNumber.replace(/\D/g, '');
    if (cleanedNumber.length !== 10) {
      Alert.alert('Error', 'Please enter a valid 10-digit phone number.');
      return;
    }
    navigation.navigate('CodeVerification', { phoneNumber: cleanedNumber });
  };

  return (
    <SafeAreaView style={styles.container}>
      <OrnamentBackground />

      <View style={styles.topSection}>
        <Image source={require('../../assets/logo.png')} style={styles.logo} />
        <Text style={styles.welcomeText}>Two-Step Verification</Text>
        <Text style={styles.description}>
          In order to create your account, 2-Step verification is required due to the sensitive nature of the information you are potentially saving on the app.
        </Text>
      </View>

      <View style={styles.mainContainer}>
        <View style={styles.phoneContainer}>
          <Image
            source={require('../../assets/icons/phone.png')}
            style={styles.phoneIcon}
          />
          <View style={styles.phoneTextContainer}>
            <Text style={styles.phoneText}>Enter Your Phone Number</Text>
            <Text style={styles.phoneSubText}>We'll send you a verification code</Text>
          </View>
        </View>

        <View style={styles.inputContainer}>
          <ScrollView style={styles.scrollView}>
            <View style={styles.inputGroup}>
              <Text style={styles.label}>Phone Number</Text>
              <TextInput
                style={styles.input}
                placeholder="(123) 456-7890"
                value={phoneNumber}
                onChangeText={handlePhoneNumberChange}
                keyboardType="phone-pad"
                placeholderTextColor="#A9A9A9"
                maxLength={14} // (123) 456-7890 is 14 characters
              />
              <Text style={styles.inputHint}>10 Numbers minimum</Text>
            </View>

            <View style={styles.buttonContainer}>
              <TouchableOpacity
                style={styles.continueButton}
                onPress={handleContinue}
              >
                <Text style={styles.continueButtonText}>Continue</Text>
              </TouchableOpacity>
            </View>

            <View style={styles.bottomPadding} />
          </ScrollView>
        </View>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
    flex: 0.18,
  },
  logo: {
    width: s(120),
    height: s(120),
    marginBottom: ms(16),
  },
  welcomeText: {
    fontSize: ms(28, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
  },
  description: {
    fontSize: ms(16, 0.3),
    color: '#FFFFFF',
    textAlign: 'center',
    marginBottom: ms(20),
    paddingHorizontal: ms(40),
  },
  mainContainer: {
    flex: 0.88,
    position: 'relative',
  },
  phoneContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#2C4364',
    padding: ms(35),
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    justifyContent: 'space-between',
    marginBottom: ms(-10),
    position: 'relative',
    zIndex: 1,
  },
  phoneIcon: {
    width: s(50),
    height: s(50),
    marginRight: ms(20),
  },
  phoneTextContainer: {
    flex: 1,
  },
  phoneText: {
    fontSize: ms(16, 0.3),
    color: '#FFFFFF',
    fontWeight: 'bold',
    marginBottom: ms(4),
  },
  phoneSubText: {
    fontSize: ms(14, 0.3),
    color: '#FFFFFF',
    opacity: 0.8,
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(30),
    position: 'relative',
    zIndex: 2,
  },
  scrollView: {
    paddingHorizontal: ms(16),
  },
  inputGroup: {
    marginBottom: ms(16),
  },
  label: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#6B7280',
    marginBottom: ms(8),
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(10),
    padding: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  inputHint: {
    fontSize: ms(12, 0.3),
    color: '#A9A9A9',
    marginTop: ms(4),
  },
  buttonContainer: {
    paddingVertical: ms(20),
    paddingHorizontal: ms(16),
    marginTop: 'auto',
  },
  continueButton: {
    backgroundColor: '#29BFC0',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  continueButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  bottomPadding: {
    height: s(30),
  },
});

export default PhoneVerificationScreen;